import React, { useMemo, useState } from 'react';
import {
  Col, Row, Typography, Button, Space, Tabs,
  Tooltip,
  IMenuProps,
  Dropdown,
} from '@revfluence/fresh';
import { AngleDownIcon, ClipboardListCheckIcon, GearIcon } from '@revfluence/fresh-icons/regular/esm';
import { useHistory } from 'react-router-dom';
import { BagShoppingIcon } from '@revfluence/fresh-icons/regular/esm';
import { useClientFeatureEnabled } from '@frontend/app/hooks';
import { ClientFeature } from '@frontend/app/constants';
import { Collections } from '../Collections/Collections';
import styles from './Products.scss';
import { useProductsContext } from '../ProductsContext';
import { ShopifyImportModal } from '../ShopifyImportModal/ShopifyImportModal';
// import { AllProductsWrapper } from '../AllProducts/AllProducts';
import { ImportSettingsDrawer } from '../ImportSettings/ImportSettingsDrawer';
import { pfaV2Routes } from '../../routes';
import { ShopifyImportModalProvider } from '../ShopifyImportModal/ShopifyImportModalContext';
import { FindAndImportProvider } from '../FindAndImport/FindAndImportContext';
import { FindAndImportModal } from '../FindAndImport/FindAndImport';
import { CSVCollectionUploadModal } from '../../../NonShopifyPFA/CSVCollectionUploadModal';
import { useImportedCollectionType } from '../../context/ImportedCollectionTypeContext';

const { Title, Text } = Typography;

export const Products = () => {
  const {
 setIsOpen, setIsImportSettingsDrawerOpen, setIsFindAndImportOpen, isShopifyConnected,
} = useProductsContext();
  const history = useHistory();
  const [isProductUploadModalOpen, setIsProductUploadModalOpen] = useState(false);

  // Check feature flag for non-Shopify PFA
  const nonShopifyPfaEnabled = useClientFeatureEnabled(ClientFeature.NON_SHOPIFY_PFA);

  // Get imported collection type from context
  const { importedCollectionType: contextImportedCollectionType } = useImportedCollectionType();

  // Override collection type based on feature flag
  const importedCollectionType = nonShopifyPfaEnabled ? contextImportedCollectionType : 'SHOPIFY';

  const isCSVStore = useMemo(() => {
    return importedCollectionType === 'CSV' && nonShopifyPfaEnabled;
  }, [importedCollectionType, nonShopifyPfaEnabled]);

  const items = useMemo<IMenuProps['items']>(() => {
    const allItems = [
      // Only show Shopify options if Shopify is connected
      ...(isShopifyConnected ? [
        {
          key: 'import',
          label: 'Import Collections',
          onClick: () => setIsOpen(true),
        },
        {
          key: 'findAndImport',
          label: 'Find and Import',
          onClick: () => setIsFindAndImportOpen(true),
        },
      ] : []),
      ...(nonShopifyPfaEnabled ? [{
        key: 'uploadCsv',
        label: 'Upload CSV',
        onClick: () => {
          setIsProductUploadModalOpen(true);
        },
      }] : []),
    ];

    // If feature flag is disabled, only show Shopify options (and only if connected)
    if (!nonShopifyPfaEnabled) {
      return allItems.filter((item) => (item.key === 'import' || item.key === 'findAndImport') && isShopifyConnected);
    }

    // Filter items based on importedCollectionType when feature flag is enabled
    if (importedCollectionType === 'CSV') {
      // Only show Upload CSV option
      return allItems.filter((item) => item.key === 'uploadCsv');
    } else if (importedCollectionType === 'SHOPIFY') {
      // Only show Shopify options (Import Collections and Find and Import) if connected
      return allItems.filter((item) => (item.key === 'import' || item.key === 'findAndImport') && isShopifyConnected);
    }

    // If null or loading, show all options (when feature flag is enabled) but respect Shopify connection
    return allItems;
  }, [setIsFindAndImportOpen, setIsOpen, setIsProductUploadModalOpen, importedCollectionType, nonShopifyPfaEnabled, isShopifyConnected]);

  return (
    <div className={`${styles.ProductFulfillmentProducts} ${styles.CSVImportModal}`}>
      <Row className={styles.header}>
        <Col>
          <Title level={3}>Products</Title>
          <Text className={styles.headerSubTitle}>
            View, add, update, and organize your collection, products and variants imported into Aspire.
          </Text>
        </Col>
        <Space className={styles.headerCTA}>
          <Tooltip title="Import Logs">
            <Button icon={<ClipboardListCheckIcon />} onClick={() => history.push(pfaV2Routes.settings.importLogs)} />
          </Tooltip>
          {!isCSVStore && (
          <Tooltip title="Settings">
            <Button icon={<GearIcon />} onClick={() => setIsImportSettingsDrawerOpen(true)} />
          </Tooltip>
          )}
          <Tooltip title={isShopifyConnected || nonShopifyPfaEnabled ? '' : 'Please connect a Shopify store to start importing products'}>
            <Dropdown menu={{ items }} disabled={!isShopifyConnected && !nonShopifyPfaEnabled}>
              <Button
                type="primary"
                icon={<BagShoppingIcon />}
                disabled={!isShopifyConnected && !nonShopifyPfaEnabled}
              >
                Import Products
                <AngleDownIcon />
              </Button>
            </Dropdown>
          </Tooltip>
        </Space>
      </Row>
      <Tabs
        defaultActiveKey="1"
        items={[
          {
            label: 'Collections',
            key: '1',
            children: <Collections />,
          },
          // {
          //   label: 'All Products',
          //   key: '2',
          //   children: <AllProductsWrapper />,
          // },
        ]}
      />
      <ShopifyImportModalProvider>
        <ShopifyImportModal />
      </ShopifyImportModalProvider>
      <FindAndImportProvider>
        <FindAndImportModal />
      </FindAndImportProvider>
      {nonShopifyPfaEnabled && (
        <CSVCollectionUploadModal
          isProductUploadModalOpen={isProductUploadModalOpen}
          setIsProductUploadModalOpen={setIsProductUploadModalOpen}
        />
      )}
      <ImportSettingsDrawer />
    </div>
  );
};
